import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { useAuth } from '../../context/AuthContext';
import PageSEOManager from '../../components/admin/PageSEOManager';
import TestimonialsManager from '../../components/admin/TestimonialsManager';
import GalleryManager from '../../components/admin/GalleryManager';
import BrandsManager from '../../components/admin/BrandsManager';
import { 
  Settings, FileText, Users, Home, Info, Mail, 
  Edit, Trash2, Plus, Save, Loader2, ChevronRight,
  Eye, Shield, BookOpen, Search, Quote, Plane, Camera, Award
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('blogs');
  const [blogs, setBlogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Wait for auth to finish loading before deciding to redirect
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, isAdmin, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all blogs
      const blogsRes = await fetch(`${API_URL}/api/blogs?status=all`, {
        credentials: 'include'
      });
      if (blogsRes.ok) {
        const blogsData = await blogsRes.json();
        setBlogs(blogsData);
      }

      // Fetch users (admin only)
      const usersRes = await fetch(`${API_URL}/api/users`, {
        credentials: 'include'
      });
      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData);
      }

      // Fetch page content
      const pagesRes = await fetch(`${API_URL}/api/pages`, {
        credentials: 'include'
      });
      if (pagesRes.ok) {
        const pagesData = await pagesRes.json();
        // Group by page_name
        const grouped = pagesData.reduce((acc, item) => {
          if (!acc[item.page_name]) acc[item.page_name] = {};
          acc[item.page_name][item.section] = item.content;
          return acc;
        }, {});
        setPageContent(grouped);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteBlog = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const response = await fetch(`${API_URL}/api/blogs/${slug}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setBlogs(blogs.filter(b => b.slug !== slug));
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
    }
  };

  const savePageContent = async (pageName, section, content) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/pages/${pageName}/${section}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: JSON.parse(content) })
      });
      if (response.ok) {
        await fetchData();
        setEditingSection(null);
        alert('Content saved successfully!');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to save. Make sure the JSON is valid.');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || (!user && !isAdmin && loading)) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">You need admin privileges to access this page.</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const tabs = [
    { id: 'blogs', label: 'Blogs', icon: FileText },
    { id: 'seo', label: 'SEO', icon: Search },
    { id: 'testimonials', label: 'Testimonials', icon: Quote },
    { id: 'brands', label: 'Brands', icon: Award },
    { id: 'travel', label: 'Travel', icon: Plane },
    { id: 'submerge', label: 'SubMerge', icon: Camera },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'pages', label: 'Pages', icon: Settings }
  ];

  const pageTemplates = {
    home: {
      hero: { title: '', subtitle: '', cta_text: '' },
      yourstory: { title: '', link: '', logo: '' }
    },
    about: {
      bio: { title: '', content: '', image: '' },
      skills: { items: [] }
    },
    contact: {
      info: { email: '', phone: '', address: '' },
      social: { linkedin: '', twitter: '', instagram: '' }
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage your website content</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                <Shield size={14} className="inline mr-1" />
                Admin
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 mb-6">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  data-testid={`admin-tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-5 py-4 text-sm font-semibold flex items-center justify-center gap-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon size={18} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* Blogs Tab */}
              {activeTab === 'blogs' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900">All Blog Posts</h2>
                    <Link
                      to="/blog/new"
                      className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                    >
                      <Plus size={18} className="mr-2" />
                      New Blog
                    </Link>
                  </div>
                  
                  <div className="space-y-4">
                    {blogs.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No blogs yet</p>
                    ) : (
                      blogs.map(blog => (
                        <div
                          key={blog.blog_id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <img
                              src={blog.featured_image}
                              alt={blog.title}
                              className="w-16 h-16 object-cover rounded-lg"
                            />
                            <div>
                              <h3 className="font-semibold text-gray-900">{blog.title}</h3>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span>{blog.author_name}</span>
                                <span>•</span>
                                <span>{blog.category}</span>
                                <span>•</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  blog.status === 'published' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {blog.status}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/blog/${blog.slug}`}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Eye size={18} />
                            </Link>
                            <Link
                              to={`/blog/edit/${blog.slug}`}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Edit size={18} />
                            </Link>
                            <button
                              onClick={() => deleteBlog(blog.slug)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* SEO Tab */}
              {activeTab === 'seo' && (
                <PageSEOManager />
              )}

              {/* Testimonials Tab */}
              {activeTab === 'testimonials' && (
                <TestimonialsManager />
              )}

              {/* Brands Tab */}
              {activeTab === 'brands' && (
                <BrandsManager />
              )}

              {/* Travel Tab */}
              {activeTab === 'travel' && (
                <GalleryManager
                  type="travel"
                  title="Travel Gallery"
                  description="Upload photos from your journeys. Each image appears on the Travel page."
                  allowLocation
                />
              )}

              {/* SubMerge Tab */}
              {activeTab === 'submerge' && (
                <GalleryManager
                  type="submerge"
                  title="SubMerge Gallery"
                  description="Manage images shown on the SubMerge photography portfolio page."
                  allowLocation
                  allowCategory
                />
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Registered Users</h2>
                  <div className="space-y-4">
                    {users.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No users yet</p>
                    ) : (
                      users.map(u => (
                        <div
                          key={u.user_id}
                          className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200"
                        >
                          <div className="flex items-center gap-4">
                            <img
                              src={u.picture || 'https://via.placeholder.com/40'}
                              alt={u.name}
                              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow"
                            />
                            <div>
                              <h3 className="font-semibold text-gray-900">{u.name}</h3>
                              <p className="text-sm text-gray-500">{u.email}</p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            u.role === 'admin'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {u.role}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Pages Tab */}
              {activeTab === 'pages' && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Edit Page Content</h2>
                  <p className="text-gray-600 mb-6">
                    Edit page content using JSON format. Changes will be reflected on the website.
                  </p>
                  
                  <div className="space-y-6">
                    {Object.entries(pageTemplates).map(([pageName, sections]) => (
                      <div key={pageName} className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                          {pageName === 'home' && <Home size={20} className="mr-2 text-blue-600" />}
                          {pageName === 'about' && <Info size={20} className="mr-2 text-blue-600" />}
                          {pageName === 'contact' && <Mail size={20} className="mr-2 text-green-600" />}
                          {pageName.charAt(0).toUpperCase() + pageName.slice(1)} Page
                        </h3>
                        
                        <div className="space-y-3">
                          {Object.entries(sections).map(([sectionName, template]) => {
                            const currentContent = pageContent[pageName]?.[sectionName] || template;
                            const isEditing = editingSection === `${pageName}-${sectionName}`;
                            
                            return (
                              <div key={sectionName} className="bg-white rounded-lg border border-gray-200 p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-700 capitalize">
                                    {sectionName.replace('_', ' ')}
                                  </span>
                                  {!isEditing ? (
                                    <button
                                      onClick={() => {
                                        setEditingSection(`${pageName}-${sectionName}`);
                                        setEditContent(JSON.stringify(currentContent, null, 2));
                                      }}
                                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                      <Edit size={14} className="inline mr-1" />
                                      Edit
                                    </button>
                                  ) : (
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => setEditingSection(null)}
                                        className="text-sm text-gray-500 hover:text-gray-700"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => savePageContent(pageName, sectionName, editContent)}
                                        disabled={saving}
                                        className="text-sm text-green-600 hover:text-green-700 font-medium"
                                      >
                                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="inline mr-1" />}
                                        Save
                                      </button>
                                    </div>
                                  )}
                                </div>
                                
                                {isEditing ? (
                                  <textarea
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    rows={8}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter JSON content..."
                                  />
                                ) : (
                                  <pre className="text-xs text-gray-500 bg-gray-50 p-2 rounded overflow-auto max-h-32">
                                    {JSON.stringify(currentContent, null, 2)}
                                  </pre>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
